import React, { useState, useEffect } from 'react';
import { format, getMonth, getYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCalendar } from '../context/CalendarContext';
import MonthView from './MonthView';
import { MonthlyStats, ColorOption } from '../types'; // Assuming MonthlyStats and ColorOption are relevant

interface MonthCardProps {
  monthDate: Date;
  monthKey: string;
  stats: MonthlyStats;
  updateMonthlyDemand: (monthKey: string, newDemand: number) => void;
}

const MonthCard: React.FC<MonthCardProps> = ({ monthDate, monthKey, stats, updateMonthlyDemand }) => {
  const year = getYear(monthDate);
  const month = getMonth(monthDate);

  const [isEditingDemand, setIsEditingDemand] = useState(false);
  const [editableDemand, setEditableDemand] = useState(stats.demand.toString());

  useEffect(() => {
    setEditableDemand(stats.demand.toString());
  }, [stats.demand]);

  const handleDemandChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditableDemand(event.target.value);
  };

  const handleDemandSave = () => {
    const newDemand = parseInt(editableDemand, 10);
    if (!isNaN(newDemand) && newDemand >= 0) {
      updateMonthlyDemand(monthKey, newDemand);
    }
    setIsEditingDemand(false);
  };

  const handleDemandKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleDemandSave();
    }
    if (event.key === 'Escape') {
      setEditableDemand(stats.demand.toString());
      setIsEditingDemand(false);
    }
  };

  let occupationColor = 'text-gray-700';
  if (stats.occupation === Infinity || (stats.capacity === 0 && stats.demand > 0)) {
    occupationColor = 'text-red-700 font-bold';
  } else if (stats.occupation > 100.9) {
    occupationColor = 'text-red-700 font-bold';
  } else if (stats.occupation >= 99.1 && stats.occupation <= 100.9) {
    occupationColor = 'text-green-600 font-semibold';
  } else if (stats.occupation < 99.1) {
    occupationColor = 'text-yellow-500 font-semibold';
  }

  return (
    <div className="bg-white shadow-lg rounded-lg p-3 flex flex-col h-full border border-gray-200">
      <h3 className="text-md sm:text-lg font-semibold text-center mb-1 capitalize text-gray-700">
        {format(monthDate, 'MMMM', { locale: ptBR })}
      </h3>
      <div className="text-xs text-center text-gray-600 mb-2 space-y-0.5">
        <div onClick={() => setIsEditingDemand(true)} className="cursor-pointer">
          Demanda:
          {isEditingDemand ? (
            <input
              type="number"
              value={editableDemand}
              onChange={handleDemandChange}
              onBlur={handleDemandSave}
              onKeyDown={handleDemandKeyDown}
              className="font-medium w-20 text-center border border-blue-400 rounded-sm px-0.5 py-0 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              autoFocus
              min="0"
            />
          ) : (
            <span className="font-medium hover:text-blue-600 hover:underline">{stats.demand.toLocaleString('pt-BR')}</span>
          )}
        </div>
        <p>Capacidade: <span className="font-medium">{stats.capacity.toLocaleString('pt-BR')}</span></p>
        <p className={`${occupationColor}`}>
          Ocupação: {stats.occupation === Infinity ? "N/A (Cap 0)" : `${stats.occupation.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
        </p>
      </div>
      <div className="flex-grow mt-1">
        <MonthView month={month} year={year} />
      </div>
    </div>
  );
};

export default MonthCard;

